import { ConditionalRule } from './dynamic-grid-container-editor/types';

export function flattenIndexedValues(obj: any) {
  const result = { ...obj };

  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (
      !Number.isNaN(Number(key)) &&
      typeof obj[key] === 'object' &&
      obj[key] !== null
    ) {
      Object.assign(result, obj[key]);
      delete result[key]; // remove the numeric key after merging
    }
  }

  return result;
}

export function flattenFormProperties(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;

  const result: any = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // If it's a numeric key with a non-null object value, flatten it
      if (!Number.isNaN(Number(key))) {
        if (
          obj[key] !== null &&
          obj[key] !== undefined &&
          typeof obj[key] === 'object'
        ) {
          // Recursively flatten the nested object
          const flattenedNested = flattenFormProperties(obj[key]);
          Object.assign(result, flattenedNested);
        }
        // Skip null/undefined values (don't add them to result)
      } else if (
        obj[key] !== null &&
        obj[key] !== undefined &&
        typeof obj[key] === 'object'
      ) {
        // For non-numeric keys, recursively flatten if it's an object
        result[key] = flattenFormProperties(obj[key]);
      } else {
        // For non-numeric keys with primitive values
        result[key] = obj[key];
      }
    }
  }

  return result;
}

// Helper function to resolve complex paths like 'marketplaceOffers.[0].name' or 'placementDrivers.2.description'
function resolvePath(
  path: string,
  marketplaceOffers: any[],
  context: Record<string, any>
): any {
  // Handle marketplace offers paths
  if (path.startsWith('marketplaceOffers.')) {
    const pathParts = path.split('.');
    // Remove 'marketplaceOffers' from the beginning
    pathParts.shift();

    let current: any = marketplaceOffers;
    // Use reduce instead of for...of loop to avoid linter issues
    const result = pathParts.reduce((acc: any, part: string) => {
      if (part.startsWith('[') && part.endsWith(']')) {
        // Handle array index like [0]
        const index = parseInt(part.slice(1, -1), 10);
        if (!Array.isArray(acc) || index < 0 || index >= acc.length) {
          return undefined;
        }
        return acc[index];
      }
      // Handle object property
      if (acc === null || acc === undefined) {
        return undefined;
      }
      return acc[part];
    }, current);
    current = result;
    return current;
  }

  // Handle regular context variables with dot notation
  // First check if it exists as a direct key
  if (path in context) {
    return context[path];
  }

  // Otherwise, traverse the path
  const pathParts = path.split('.');
  return pathParts.reduce((acc: any, part: string) => {
    if (acc === null || acc === undefined) {
      return undefined;
    }
    // Handle array index notation like "2" in "placementDrivers.2"
    if (!Number.isNaN(Number(part)) && Array.isArray(acc)) {
      const index = parseInt(part, 10);
      if (index < 0 || index >= acc.length) {
        return undefined;
      }
      return acc[index];
    }
    return acc[part];
  }, context);
}

// Safe expression evaluator for basic conditional logic
function evaluateSimpleExpression(
  expression: string,
  marketplaceOffers: any[],
  context: Record<string, any>
): boolean {
  // Remove extra whitespace
  const cleanExpression = expression.trim();

  // Handle simple boolean values
  if (cleanExpression === 'true') return true;
  if (cleanExpression === 'false') return false;

  // Handle OR conditions (||)
  if (cleanExpression.includes('||')) {
    return cleanExpression
      .split('||')
      .some((part) =>
        evaluateSimpleExpression(part.trim(), marketplaceOffers, context)
      );
  }

  // Handle AND conditions (&&)
  if (cleanExpression.includes('&&')) {
    return cleanExpression
      .split('&&')
      .every((part) =>
        evaluateSimpleExpression(part.trim(), marketplaceOffers, context)
      );
  }

  // Handle comparison operations - updated regex to handle complex paths
  const comparisonRegex =
    /^([a-zA-Z_][a-zA-Z0-9_.[\]]*)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/;
  const match = cleanExpression.match(comparisonRegex);

  if (match) {
    const [, leftOperand, operator, rightOperand] = match;
    let leftValue = resolvePath(leftOperand, marketplaceOffers, context);
    let rightValue: any = rightOperand.trim();

    // If leftValue is an array, use its length for comparison
    if (Array.isArray(leftValue)) {
      leftValue = leftValue.length;
    }

    // Parse right operand
    if (rightValue.startsWith("'") && rightValue.endsWith("'")) {
      rightValue = rightValue.slice(1, -1); // Remove quotes for string
    } else if (rightValue.startsWith('"') && rightValue.endsWith('"')) {
      rightValue = rightValue.slice(1, -1); // Remove quotes for string
    } else if (rightValue === 'null') {
      rightValue = null; // Convert string 'null' to actual null
    } else if (rightValue === 'undefined') {
      rightValue = undefined; // Convert string 'undefined' to actual undefined
    } else if (!Number.isNaN(Number(rightValue))) {
      rightValue = Number(rightValue); // Convert to number
    } else if (rightValue === 'true' || rightValue === 'false') {
      rightValue = rightValue === 'true'; // Convert to boolean
    }

    // Perform comparison
    let result: boolean;
    switch (operator) {
      case '===':
        result = leftValue === rightValue;
        break;
      case '!==':
        result = leftValue !== rightValue;
        break;
      case '==':
        result = leftValue == rightValue; // eslint-disable-line eqeqeq
        break;
      case '!=':
        result = leftValue != rightValue; // eslint-disable-line eqeqeq
        break;
      case '>=':
        result = leftValue >= rightValue;
        break;
      case '<=':
        result = leftValue <= rightValue;
        break;
      case '>':
        result = leftValue > rightValue;
        break;
      case '<':
        result = leftValue < rightValue;
        break;
      default:
        result = false;
    }

    return result;
  }

  // Handle simple variable evaluation
  const resolvedValue = resolvePath(
    cleanExpression,
    marketplaceOffers,
    context
  );
  if (resolvedValue !== undefined) {
    // If the resolved value is an array, evaluate based on its length
    if (Array.isArray(resolvedValue)) {
      return resolvedValue.length > 0;
    }
    return Boolean(resolvedValue);
  }

  // If we can't parse it safely, return true (show by default)
  return true;
}

export const evaluateConditionalShowRules = (
  conditionalString: string,
  marketplaceOffers: any[] = [],
  context: Record<string, any> = {}
): boolean => {
  if (!conditionalString || typeof conditionalString !== 'string') {
    return true; // Show by default if no rules
  }

  try {
    // Create a safe evaluation context
    const evalContext = {
      pathname:
        context.pathname ||
        (typeof window !== 'undefined' ? window?.location?.pathname : '') ||
        '',
      userAgent:
        context.userAgent ||
        (typeof navigator !== 'undefined' ? navigator?.userAgent : '') ||
        '',
      screenWidth:
        context.screenWidth ||
        (typeof window !== 'undefined' ? window?.innerWidth : 0) ||
        0,
      screenHeight:
        context.screenHeight ||
        (typeof window !== 'undefined' ? window?.innerHeight : 0) ||
        0,
      ...context,
    };

    // Simple safe evaluation for basic conditional expressions
    // This handles common patterns like: pathname === '/some-path' || screenWidth > 768
    // And now also handles marketplace offers paths like: marketplaceOffers.[0].name === 'SomeName'
    return evaluateSimpleExpression(
      conditionalString,
      marketplaceOffers,
      evalContext
    );
  } catch (error) {
    // Use proper error handling instead of console.warn
    if (process.env.NODE_ENV === 'development') {
      // Only log in development mode
      // eslint-disable-next-line no-console
      console.warn('Error evaluating conditional show rules:', error);
    }
    return true; // Show by default if evaluation fails
  }
};

const formatValueByType = (value: any, attributeType?: string): string => {
  // Don't wrap null or undefined in quotes, even if attributeType is 'string'
  if (
    value === 'null' ||
    value === 'undefined' ||
    value === null ||
    value === undefined
  ) {
    return String(value);
  }
  if (attributeType === 'string') {
    return `'${value}'`;
  }
  return String(value);
};

export const formatConditionalRulesAsString = (
  rules: ConditionalRule[]
): string | undefined => {
  if (!rules || rules.length === 0) return undefined;

  const conditions = rules?.filter((rule) => rule.type === 'condition');
  const logicalOperators = rules.filter(
    (rule) => rule.type === 'logicalOperator'
  );

  if (conditions.length === 0) return undefined;

  let result = `${conditions[0].attribute} ${
    conditions[0].operator
  } ${formatValueByType(conditions[0].value, conditions[0].attributeType)}`;

  if (conditions.length > 1) {
    for (let i = 1; i < conditions.length; i += 1) {
      const logicalOp = logicalOperators[i - 1];
      if (logicalOp) {
        result += ` ${logicalOp.operator} ${conditions[i].attribute} ${
          conditions[i].operator
        } ${formatValueByType(
          conditions[i].value,
          conditions[i].attributeType
        )}`;
      }
    }
  }

  return result;
};
